import Box from "@mui/material/Box"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import PropTypes from "prop-types"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import TextSkeleton from "../../../common/components/TextSkeleton"
import { getAdminLiquidCardSx } from "../adminStyles"

const StatCardPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "clickable",
})(({ theme, clickable }) => ({
  ...getAdminLiquidCardSx(),
  position: "relative",
  padding: theme.spacing(2.5),
  height: "100%",
  minHeight: 118,
  transition: theme.transitions.create(["box-shadow", "outline"], {
    duration: 150,
    easing: "ease-in-out",
  }),
  ...(clickable && {
    cursor: "pointer",
    "&:hover, &:focus-visible": {
      boxShadow:
        "0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)",
      outline: `2px solid ${theme.palette.primary.main}`,
    },
  }),
  [theme.breakpoints.only("xs")]: {
    padding: theme.spacing(2),
    minHeight: 96,
  },
}))

const IconHalo = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 52,
  height: 52,
  borderRadius: "16px",
  backgroundColor: theme.palette.colors.brand100,
  color: theme.palette.colors.brand600,
  flexShrink: 0,
  [theme.breakpoints.only("xs")]: {
    width: 44,
    height: 44,
    borderRadius: "14px",
  },
}))

export default function StatCard({
  label,
  value,
  sublabel,
  icon: Icon,
  accent = "brand",
  loading = false,
  onClick,
}) {
  const accentColors = {
    brand: {
      halo: "colors.brand100",
      icon: "colors.brand600",
    },
    success: {
      halo: "colors.success50",
      icon: "colors.success600",
    },
    neutral: {
      halo: "colors.gray100",
      icon: "colors.gray600",
    },
    info: {
      halo: "#DBEAFE",
      icon: "#1D4ED8",
    },
    warning: {
      halo: "#FEF3C7",
      icon: "#B45309",
    },
    danger: {
      halo: "#FEE2E2",
      icon: "#B91C1C",
    },
  }
  const palette = accentColors[accent] || accentColors.brand

  return (
    <StatCardPaper
      elevation={0}
      clickable={Boolean(onClick)}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                onClick()
              }
            }
          : undefined
      }
    >
      <Row
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          height: "100%",
          gap: 2,
        }}
      >
        <Column sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            {label}
          </Typography>
          {loading ? (
            <Box sx={{ mt: 0.75 }}>
              <TextSkeleton width={8} />
            </Box>
          ) : (
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "1.75rem", sm: "2.125rem" },
                fontWeight: 700,
                color: "colors.gray900",
                lineHeight: 1.1,
                mt: 0.5,
              }}
            >
              {typeof value === "number" ? value.toLocaleString() : value}
            </Typography>
          )}
          {sublabel ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {sublabel}
            </Typography>
          ) : null}
        </Column>
        {Icon ? (
          <IconHalo sx={{ bgcolor: palette.halo, color: palette.icon }}>
            <Icon sx={{ fontSize: { xs: 22, sm: 26 } }} />
          </IconHalo>
        ) : null}
      </Row>
    </StatCardPaper>
  )
}

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sublabel: PropTypes.string,
  icon: PropTypes.elementType,
  accent: PropTypes.oneOf(["brand", "success", "neutral"]),
  loading: PropTypes.bool,
  onClick: PropTypes.func,
}
